import { Coins, Info, Plus, RotateCcw, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button, buttonClasses } from '../components/ui/Button';
import { PageHeader, Skeleton } from '../components/ui/Feedback';
import { useMe } from '../hooks/useAuth';
import { cn } from '../lib/cn';

export function CreditsPage() {
  const { data: me, isLoading } = useMe();
  if (isLoading || !me) return <Skeleton className="h-64 w-full" />;
  const empty = me.credits === 0;

  return (
    <div className="max-w-3xl">
      <PageHeader title="Credits" color="green" description="Each Pull Request review uses one credit." />

      <section className="card relative overflow-hidden p-6" aria-labelledby="balance-heading">
        <span className={cn('absolute inset-x-0 top-0 h-1', empty ? 'bg-chalk-pink' : 'bg-chalk-green')} aria-hidden />
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 id="balance-heading" className="text-sm font-medium text-muted">
              Your balance
            </h2>
            <p className="mt-1 flex items-baseline gap-2">
              <span className={cn('chalk-title text-6xl', empty ? 'text-chalk-pink' : 'text-chalk-green')}>{me.credits}</span>
              <span className="text-sm text-muted">review credit{me.credits === 1 ? '' : 's'}</span>
            </p>
            {empty && <p className="mt-2 text-sm text-chalk-pink">You need at least one credit to start a review.</p>}
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <Button variant="primary" disabled icon={<Plus className="h-4 w-4" />} aria-describedby="add-credits-note">
              Add credits
            </Button>
            <span id="add-credits-note" className="rounded-full bg-chalk-lilac/15 px-2.5 py-0.5 text-xs font-medium text-chalk-lilac">
              Coming soon
            </span>
          </div>
        </div>
      </section>

      <section className="mt-6 grid gap-4 sm:grid-cols-3" aria-label="How credits work">
        {[
          { icon: Coins, color: 'text-chalk-yellow', title: '1 credit per review', body: 'Charged when a review starts. Opening a review that is already running is free.' },
          { icon: RotateCcw, color: 'text-chalk-blue', title: 'Failed runs are refunded', body: 'If a review fails, the credit goes straight back to your balance. Retrying uses one again.' },
          { icon: Sparkles, color: 'text-chalk-pink', title: 'Triage is always free', body: 'Reading findings, filtering, dismissing and publishing comments never uses credits.' },
        ].map(({ icon: Icon, color, title, body }) => (
          <div key={title} className="card p-5">
            <Icon className={cn('h-5 w-5', color)} aria-hidden />
            <h3 className="mt-3 text-sm font-semibold">{title}</h3>
            <p className="mt-1 text-sm text-muted">{body}</p>
          </div>
        ))}
      </section>

      <p className="mt-6 flex items-start gap-2 text-sm text-muted">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-chalk-blue" aria-hidden />
        Buying credits isn&apos;t available yet. When it is, you&apos;ll be able to top up from this page.
      </p>
      {!empty && (
        <Link to="/app/new" className={cn(buttonClasses('secondary'), 'mt-6')}>
          Start a review
        </Link>
      )}
    </div>
  );
}
